import { Box, FormControlLabel, FormHelperText, Grid, Radio, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import AppDialog from "../../../components/Common/AppDialog";
import CustomSelect from "../../../components/Form/CustomSelect";
import CustomTextField from "../../../components/Form/CustomTextField";
import TablePageControl from "../../../components/Common/TablePageControl";
import QuotationVehicleList from "./QuotationVehicleList";

const vehicleSources = [
  { id: "manual", name: "Enter manually", description: "Select make and model" },
  { id: "listing", name: "From listing", description: "Pick from your saved listing" },
  { id: "inventory", name: "From inventory", description: "Pick from your saved inventory" },
];

const VehicleDetails = ({ isEdit, formObj, handleFormObjChange, makeList, error }) => {
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehiclePagination, setVehiclePagination] = useState(null);
  const modelList = makeList?.find((make) => String(make.id) === String(formObj.make))?.models || [];
  const fieldError = (field) => {
    if (error?.code !== "invalid_field") return null;
    const message = error?.errors?.[field];
    return Array.isArray(message) ? message[0] : message;
  };
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const selectedVehicleError = formObj.vehicleSource === "listing"
    ? fieldError("bike_advert_id")
    : fieldError("inventory_id");

  const vehicleType = isEdit ? formObj?.bike_advert_type?.charAt(0)?.toUpperCase() + formObj?.bike_advert_type?.slice(1) : formObj.vehicle?.[formObj?.vehicleSource === "listing" ? "bike_advert_type" : "inventory_type"]?.charAt(0)?.toUpperCase() + formObj.vehicle?.[formObj?.vehicleSource === "listing" ? "bike_advert_type" : "inventory_type"]?.slice(1) || "-"

  const selectSource = (source) => {
    if (formObj.vehicleSource === source) return;

    handleFormObjChange("vehicleSource", source);
    handleFormObjChange("make", "");
    handleFormObjChange("model", "");
    handleFormObjChange("registrationNo", "");
    handleFormObjChange("year", "");
    handleFormObjChange("vehicle", null);
    handleFormObjChange("bike_advert_type", "");
    handleFormObjChange("relatedLink", "");
    setVehicleDialogOpen(false);
  };

  const selectLinkedVehicle = (field, value) => {
    if (field !== "vehicle") return;
    handleFormObjChange("vehicle", value);
    handleFormObjChange(
      "bike_advert_type",
      formObj.vehicleSource === "listing" ? value?.bike_advert_type || "" : value?.inventory_type || ""
    );
    if (formObj.vehicleSource === "listing" && value?.related_link) {
      handleFormObjChange("relatedLink", value.related_link);
    }
  };

  return (
    <Box>
      <Typography fontSize={15} fontWeight={600} mb={1}>
        Select vehicle
      </Typography>
      <Typography fontSize={14} color="#808588" mb={2}>
        Link this quotation to a specific vehicle so the make and model are recorded with the prospect.
      </Typography>

      <Grid container spacing={1.5} mb={1.25}>
        {vehicleSources.map((source) => (
          <Grid item md={4} xs={12} key={source.id}>
            <Box
              onClick={() => selectSource(source.id)}
              sx={{
                minHeight: 80,
                p: 2,
                border: `1px solid ${formObj.vehicleSource === source.id ? "#1D1B1B" : "#E6E6E6"}`,
                borderRadius: "10px",
                backgroundColor: formObj.vehicleSource === source.id ? "#F5F5F5" : "#FFFFFF",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Grid container alignItems="center">
                <Grid item xs={10}>
                  <Typography fontSize={14} fontWeight={500}>{source.name}</Typography>
                  <Typography fontSize={12} color="#808588" mt={0.5}>{source.description}</Typography>
                </Grid>
                <Grid item xs={2} display="flex" justifyContent="flex-end">
                  <FormControlLabel
                    checked={formObj.vehicleSource === source.id}
                    onChange={() => selectSource(source.id)}
                    control={<Radio size="small" sx={{ "&.Mui-checked": { color: "#1D1B1B" } }} />}
                    label=""
                    sx={{ m: 0 }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>
      {fieldError("vehicle_source") && <FormHelperText sx={{ color: "#BA2727", mt: 0.5 }}>{fieldError("vehicle_source")}</FormHelperText>}
      {formObj.vehicleSource === "manual" ? (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <CustomSelect
              values={formObj?.bike_advert_type || ""}
              onChange={(value) => {
                handleFormObjChange("bike_advert_type", value);
              }}
              data={[{ id: "new", name: "New" }, { id: "used", name: "Used" }, { id: "rental", name: "Rental" }]}
              placeholder="Vehicle Type"
              ableClear={false}
              height="58px"
              valueName="id"
              labelName="name"
              // error={fieldError("bike_advert_type") || fieldError("vehicle_type")}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <CustomSelect
              values={formObj.make}
              onChange={(value) => {
                handleFormObjChange("make", value);
                handleFormObjChange("model", "");
              }}
              data={makeList}
              placeholder="Make"
              labelName="name"
              valueName="id"
              ableClear={false}
              height="58px"
              error={fieldError("bike_make_id")}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <CustomSelect
              values={formObj.model}
              onChange={(value) => handleFormObjChange("model", value)}
              data={modelList}
              disabled={!formObj.make}
              placeholder="Model"
              labelName="name"
              valueName="id"
              ableClear={false}
              height="58px"
              error={fieldError("bike_model_id")}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <CustomTextField
              values={formObj.year}
              onChange={(value) => handleFormObjChange("year", value)}
              title="Year of manufacture"
              isOptional={true}
              type="integer"
              max={new Date().getFullYear()}
              marginTop={0}
              borderRadius={2}
              error={fieldError("manu_year")}
            />
          </Grid>
          {formObj?.bike_advert_type !== "new" &&
            <Grid item md={6} xs={12}>
              <CustomTextField
                values={formObj.registrationNo}
                onChange={(value) => handleFormObjChange("registrationNo", value)}
                title="Registration number"
                marginTop={0}
                isOptional={true}
                borderRadius={2}
                error={fieldError("register_number")}
              />
            </Grid>}
        </Grid>
      ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={8}>
            <Box
              onClick={() => setVehicleDialogOpen(true)}
              sx={{
                minHeight: 58,
                px: 2,
                mt: 0.8,
                border: `1.5px solid ${selectedVehicleError ? "#BA2727" : "#E6E6E6"}`,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                backgroundColor: "#FFFFFF",
              }}
            >
              <Typography fontSize={14} color={formObj.vehicle?.id ? "#1D1B1B" : "#BDBDBD"}>
                {formObj?.vehicle ? formObj?.vehicle?.make + "/" + formObj?.vehicle?.model : "Select vehicle"}
              </Typography>
              <i className="ri-arrow-down-s-line" style={{ color: "#BDBDBD" }} />
            </Box>
            {selectedVehicleError && <FormHelperText sx={{ color: "#BA2727", mt: 0.5 }}>{selectedVehicleError}</FormHelperText>}
          </Grid>
          <Grid item xs={4}>
            <CustomTextField
              values={vehicleType}
              height="58px"
              marginTop={0}
              title={"Vehicle type"}
              borderRadius={2}
              disabled={true}
              error={fieldError("related_link")}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              values={formObj.relatedLink}
              onChange={(value) => handleFormObjChange("relatedLink", value)}
              placeholder="Related Link"
              height="58px"
              marginTop={0}
              borderRadius={2}
              error={fieldError("related_link")}
            />
          </Grid>
        </Grid>
      )}
      {["listing", "inventory"].includes(formObj.vehicleSource) && <AppDialog
        open={vehicleDialogOpen}
        close={() => setVehicleDialogOpen(false)}
        title={`Select vehicle`}
        width="80%"
        fullWidth
        isMobile={window.innerWidth <= 768}
        hideCloseIcon={false}
        contentSx={{ pb: 3 }}
        customTitle={vehiclePagination && !vehiclePagination.loading ? (
          <Box display="flex" justifyContent="center" width="100%">
            <TablePageControl
              length={vehiclePagination.length}
              setLength={vehiclePagination.setLength}
              page={vehiclePagination.page}
              setPage={vehiclePagination.setPage}
              totalPage={vehiclePagination.totalPage}
              justifyContent="center"
            />
          </Box>
        ) : null}
      >
        <QuotationVehicleList
          key={formObj.vehicleSource}
          formObj={{
            advert_type: formObj.vehicleSource,
            vehicle: formObj.vehicle,
          }}
          isEdit={isEdit}
          handleFormChange={selectLinkedVehicle}
          handleVehicleObjChange={() => { }}
          setIsLoading={() => { }}
          isSingPass={false}
          onSelect={() => setVehicleDialogOpen(false)}
          onPaginationChange={isEdit ? undefined : setVehiclePagination}
        />
      </AppDialog>}
    </Box>
  );
};

export default VehicleDetails;
